import React from 'react'
import { Button, Flex, Icon, Text } from '@sendoutcards/quantum-design-ui'
import {
  useCallback,
  useEffect,
  usePersistedStateV2,
  useState,
} from 'src/hooks'
import {
  DigitalGiftCardForRedemptionFragment,
  PhoneNumberInput,
  VerifyPhoneNumberResourceType,
} from 'src/graphql/generated/graphql'
import { PhoneNumberVerificationDialogGetActJWT } from 'src/phone_number_verification/PhoneNumberVerificationDialogGetActJWT'
import {
  useDigitalCardAddPhoneNumberToAccount,
  useDigitalCardRecipientLinkToAccount,
  useDigitalGiftCardProduct,
  useDigitalGiftCardsRedeem,
  useMarketingContent,
} from 'src/react_query'
import { Transition } from 'src/chrome'
import { Dialog } from 'src/design_system/molecules/dialog/Dialog'
import { ClientError } from 'graphql-request'
import { GiftCardRedemptionDialog } from './GiftCardRedemptionDialog'

export type GiftCardRedemptionProps = {
  giftCardImage?: string
  giftCardTitle?: string
  giftCardFaceValue?: string
  giftCardCurrency?: string
  token: string
  allowedPhoneNumber?: PhoneNumberInput
  onClose: () => void
}

export const GiftCardRedemption: React.FC<GiftCardRedemptionProps> = ({
  giftCardImage,
  giftCardTitle,
  giftCardFaceValue,
  giftCardCurrency,
  token,
  allowedPhoneNumber,
  onClose,
}) => {
  const [redeemCodeVerificationToken, setRedeemCodeVerificationToken] =
    usePersistedStateV2<string | undefined>(`tk_${token}`, undefined)

  const recipientLinkToAccount = useDigitalCardRecipientLinkToAccount()
  const digitalGiftCardsRedeem = useDigitalGiftCardsRedeem()
  const addPhoneNumberToAccount = useDigitalCardAddPhoneNumberToAccount()
  const { data: marketingContent } = useMarketingContent()

  const [giftCard, setGiftCard] =
    useState<DigitalGiftCardForRedemptionFragment>()

  // Whenever the user logs in, most of the React tree changes, resulting in a state reset for most of the components.
  // This is a workaround, right after login we set this on sessionStorage so that we can continue the linking process
  // after the reset. In the future we can show some feedback (e.g. an account has been created).
  const [hasJustLoggedIn, setHasJustLoggedIn] = usePersistedStateV2<
    string | undefined
  >('isLoggingIn', undefined, true)

  const [hasJustLinkedToUser, setHasJustLinkedToUser] = useState<boolean>(false)
  const [hasJustAddedPhoneNumber, setHasJustAddedPhoneNumber] =
    useState<boolean>(false)

  const [isWaitingTo, setIsWaitingTo] = useState<'addPhone' | 'linkAccount'>()

  // `true` means it successfully opened, `false` means it failed (e.g. popup blocker)
  const [isRedeemUrlOpenSuccess, setIsRedeemUrlOpenSuccess] = useState<
    boolean | undefined
  >()

  const digitalGiftCardProduct = useDigitalGiftCardProduct({
    code: giftCard?.productCodes[0] ?? '',
  })
  const product = digitalGiftCardProduct.data

  const linkToAccount = useCallback(
    async (redeemCodeVerificationToken: string) => {
      if (redeemCodeVerificationToken && !recipientLinkToAccount.isPending) {
        try {
          const result = await recipientLinkToAccount.mutateAsync({
            jwt: redeemCodeVerificationToken,
          })
          if (
            result.__typename === 'DigitalCardRecipientLinkToAccountSuccess'
          ) {
            setHasJustLinkedToUser(true)
            setGiftCard(undefined)
          } else if (
            result.__typename === 'DigitalCardRecipientLinkToAccountFailure'
          ) {
            return result.failureMessage
          }
        } catch (e) {
          // TODO: handle better network errors vs token errors
          setRedeemCodeVerificationToken(undefined)
          setGiftCard(undefined)
          setIsWaitingTo('linkAccount')
        }
      }
      return undefined
    },
    [
      recipientLinkToAccount,
      setRedeemCodeVerificationToken,
      setHasJustLinkedToUser,
    ],
  )

  const addPhoneToAccount = useCallback(
    async (redeemCodeVerificationToken: string) => {
      if (!addPhoneNumberToAccount.isPending) {
        try {
          const result = (
            await addPhoneNumberToAccount.mutateAsync({
              jwt: redeemCodeVerificationToken,
            })
          ).result
          if (
            result.__typename === 'DigitalCardAddPhoneNumberToAccountSuccess'
          ) {
            setHasJustAddedPhoneNumber(true)
            setGiftCard(undefined)
          } else if (
            result.__typename === 'DigitalCardAddPhoneNumberToAccountFailure'
          ) {
            if (result.failureType === 'JWT_EXPIRED') {
              setRedeemCodeVerificationToken(undefined)
              setGiftCard(undefined)
              setIsWaitingTo('addPhone')
            } else {
              return result.failureMessage
            }
          }
        } catch (e) {
          return e instanceof Error ? e.message : String(e)
        }
      }
      return undefined
    },
    [
      setRedeemCodeVerificationToken,
      addPhoneNumberToAccount,
      setHasJustAddedPhoneNumber,
    ],
  )

  useEffect(() => {
    if (hasJustLoggedIn === token) {
      // We wait a bit to actually trigger the account linking to guarantee that this is the component after the reset
      // and that this is not a duplicated effect caused by React's strict mode.
      const timeout = setTimeout(() => {
        if (redeemCodeVerificationToken !== undefined) {
          linkToAccount(redeemCodeVerificationToken)
        } else {
          setIsWaitingTo('linkAccount')
        }
        setHasJustLoggedIn(undefined)
      }, 1000)

      return () => {
        clearTimeout(timeout)
      }
    }
    return
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasJustLoggedIn])

  useEffect(() => {
    if (
      redeemCodeVerificationToken &&
      !giftCard &&
      !digitalGiftCardsRedeem.isPending
    ) {
      ;(async () => {
        try {
          const result = await digitalGiftCardsRedeem.mutateAsync({
            jwt: redeemCodeVerificationToken,
          })
          const newGiftCard = result.digitalGiftCardsRedeemed[0]
          if (newGiftCard) {
            setGiftCard(newGiftCard)
            if (
              !!newGiftCard.recipient.linkedUser &&
              result.justLinkedUser?.id === newGiftCard.recipient.linkedUser.id
            ) {
              setHasJustLinkedToUser(true)
            }
          }
        } catch (e) {
          // TODO: handle better network errors vs token errors
          setRedeemCodeVerificationToken(undefined)
        }
      })()
    }
  }, [
    digitalGiftCardsRedeem,
    setRedeemCodeVerificationToken,
    redeemCodeVerificationToken,
    giftCard,
  ])

  if (digitalGiftCardsRedeem.isPending) {
    return <Transition />
  }

  // TODO: This is a hot fix to prevent issues with the existing redemption flow if a jwt token is expired
  // we plan to add a new error type to soc backend to advise when the token has expired. Once that is done
  // we need to default to render the error message below in ALL other error cases besides the expired token
  const isLimitBlockingError =
    digitalGiftCardsRedeem.error instanceof ClientError
      ? (digitalGiftCardsRedeem.error.response.errors?.some(
          val =>
            val?.extensions?.['code'] ===
            'DIGITAL-GIFT-CARDS-REDEEM-RISK-BLOCKING',
        ) ?? false)
      : false

  if (isLimitBlockingError) {
    const msg =
      digitalGiftCardsRedeem.error instanceof ClientError
        ? digitalGiftCardsRedeem.error?.response?.errors?.[0].message
        : 'Failed to Redeem Gift Card'
    return (
      <Flex flexDirection={'column'} alignItems="center" style={{ gap: '4px' }}>
        <Icon name="exclamation" size="large" primaryColor="danger" />
        <Text color="danger" type="body">
          {msg}
        </Text>
        <Button
          onClick={() => digitalGiftCardsRedeem.reset()}
          outlined
          type="danger"
        >
          Try Again
        </Button>
      </Flex>
    )
  }

  if (
    redeemCodeVerificationToken &&
    giftCard &&
    /^(VISMA|VITVS)/.test(giftCard?.productCodes.at(0) ?? '')
  ) {
    if (isRedeemUrlOpenSuccess === undefined) {
      const res = window.open(giftCard.payout.url)
      setIsRedeemUrlOpenSuccess(res !== null)
    }
    return isRedeemUrlOpenSuccess ? (
      <Dialog
        isOpen={true}
        onClose={onClose}
        title="Gift Card redemption opened!"
        maxWidth="40em"
      >
        <Text type={'body'}>
          The{' '}
          <a
            href={giftCard.payout.url}
            target="_blank"
            rel="noopener"
            style={{ fontFamily: 'inherit' }}
          >
            redemption page
          </a>{' '}
          for your {`$${giftCard.amount}`}
          {product && ` ${product.title}`} gift card was opened in a new tab.
          <br />
        </Text>
      </Dialog>
    ) : (
      <Dialog
        isOpen={true}
        onClose={onClose}
        title="Gift Card redemption"
        maxWidth="40em"
      >
        <Text type={'body'}>
          To redeem your {`$${giftCard.amount}`}
          {product && ` ${product.title}`} gift card click on the following
          link:{' '}
          <a
            href={giftCard.payout.url}
            target="_blank"
            rel="noopener"
            style={{ fontFamily: 'inherit' }}
          >
            Redemption page.
          </a>
        </Text>
      </Dialog>
    )
  }

  if (redeemCodeVerificationToken && giftCard) {
    return (
      <GiftCardRedemptionDialog
        giftCard={giftCard}
        hasJustLinkedToUser={hasJustLinkedToUser}
        hasJustAddedPhoneNumber={hasJustAddedPhoneNumber}
        isLinkingToAccount={recipientLinkToAccount.isPending}
        linkToAccount={async () =>
          await linkToAccount(redeemCodeVerificationToken)
        }
        addPhoneToAccount={async () =>
          await addPhoneToAccount(redeemCodeVerificationToken)
        }
        setHasJustLoggedIn={v => {
          setHasJustLoggedIn(v ? token : undefined)
        }}
        onClose={onClose}
      />
    )
  }

  const getCurrencyString = () => {
    return !!giftCardCurrency
      ? giftCardCurrency === 'USD'
        ? ''
        : giftCardCurrency
      : ''
  }

  const dialogHero = (
    <Flex
      flexDirection="column"
      rowGap="x0"
      justifyContent="center"
      alignItems="center"
      maxHeight={'30dvh'}
      outset={{ bottom: 'x1' }}
    >
      <div
        style={{
          position: 'relative',
          backgroundImage: `url(${giftCardImage})`,
          backgroundPosition: 'center',
          backgroundSize: 'contain',
          backgroundRepeat: 'no-repeat',
          width: '20rem',
          aspectRatio: '54/30',
          borderRadius: '1rem',
        }}
      />
      <Flex
        justifyContent="space-between"
        alignItems="center"
        width="18rem"
        style={{ marginTop: '4px', padding: '0 0.25rem' }}
      >
        <Text
          type="body"
          content={giftCardTitle}
          style={{
            fontSize: '0.75rem',
            fontWeight: 700,
            color: '#404040',
            overflow: 'hidden',
            whiteSpace: 'pre',
            flexShrink: 1,
            maxWidth: '50%',
            textOverflow: 'ellipsis',
          }}
        />
        <div
          style={{
            backgroundColor: '#c7e8c2',
            borderRadius: '50px',
            padding: '0 8px',
            height: '100%',
          }}
        >
          <Text
            type="body"
            content={`${getCurrencyString()}$${giftCardFaceValue}`}
            style={{
              fontSize: '0.6rem',
              fontWeight: 700,
              color: '#348d26',
              lineHeight: '1.25rem',
            }}
          />
        </div>
      </Flex>
    </Flex>
  )

  return (
    <>
      {!redeemCodeVerificationToken && (
        <PhoneNumberVerificationDialogGetActJWT
          title={'Claim Your Gift Card'}
          subtitle={
            marketingContent?.giftCardPhoneVerificationTitle.content ?? ''
          }
          hero={dialogHero}
          onClose={onClose}
          resourceType={VerifyPhoneNumberResourceType.ActRecipient}
          resourceId={token}
          allowedPhoneNumber={allowedPhoneNumber}
          onSuccess={verificationToken => {
            setRedeemCodeVerificationToken(verificationToken)
            if (isWaitingTo === 'linkAccount') {
              setIsWaitingTo(undefined)
              linkToAccount(verificationToken)
            } else if (isWaitingTo === 'addPhone') {
              setIsWaitingTo(undefined)
              addPhoneToAccount(verificationToken)
            }
          }}
        />
      )}
    </>
  )
}
